import React, { useContext, useLayoutEffect, useRef, useState } from 'react';

import { SeriesFocusContext } from '../seriesFocus';

export type LegendItem = { name: string; color: string; mark: 'line' | 'dash' | 'square'; part?: string };

const LINES = 2;
const NARROW = 560;
const ONE_LINE_AT_LEAST = 3;

function measuredFit(box: HTMLElement, total: number, width: number, current: number): number {
  const nodes = [...box.querySelectorAll<HTMLElement>(':scope > .li')];
  if (!nodes.length) return current;
  const top = nodes[0].offsetTop;
  const step = nodes[0].offsetHeight;
  const onFirstLine = nodes.filter((node) => node.offsetTop < top + step / 2).length;
  const lines = width < NARROW && onFirstLine >= Math.min(total, ONE_LINE_AT_LEAST) ? 1 : LINES;
  const limit = top + step * (lines - 1) + step / 2;
  const within = nodes.filter((node) => node.offsetTop < limit).length;
  if (within < current) return Math.max(1, within);
  const more = box.querySelector<HTMLElement>(':scope > .more');
  if (more && more.offsetTop >= limit && current > 1) return current - 1;
  return current;
}

export function sameMembers(legends: Array<Array<LegendItem>>): Array<LegendItem> | undefined {
  const colors = new Map<string, LegendItem>();
  for (const items of legends)
    for (const item of items) {
      const seen = colors.get(item.name);
      if (seen && (seen.color !== item.color || seen.mark !== item.mark)) return undefined;
      colors.set(item.name, item);
    }
  const overlap = legends.every((items) => items.some((item) => legends.some((other) => other !== items && other.some((entry) => entry.name === item.name))));
  return overlap ? [...colors.values()] : undefined;
}

export default function Legend({ items, width }: { items: Array<LegendItem>; width: number; font: string }) {
  const focus = useContext(SeriesFocusContext);
  const [expanded, setExpanded] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const names = items.map((item) => item.name);
  const identity = `${names.join('|')}@${width}`;
  const [fit, setFit] = useState({ identity, count: items.length });
  const fits = fit.identity === identity ? fit.count : items.length;
  useLayoutEffect(() => {
    if (expanded || !box.current) return;
    const next = measuredFit(box.current, items.length, width || 1200, fits);
    if (next !== fits || fit.identity !== identity) setFit({ identity, count: next });
  });
  if (!items.length) return null;
  const shown = expanded ? items.length : fits;
  const rest = items.slice(shown);
  return (
    <div aria-label={focus ? 'Legend' : undefined} className="lg" data-lens-legend ref={box} role={focus ? 'group' : 'list'}>
      {items.slice(0, shown).map((item) =>
        focus ? (
          <button
            aria-pressed={!focus.hidden.has(item.name)}
            className={`li${focus.hidden.has(item.name) ? ' off' : ''}`}
            data-lens-part={item.part}
            key={item.name}
            onBlur={() => focus.hover(undefined)}
            onClick={(event) => (event.altKey ? focus.isolate(item.name, names) : focus.toggle(item.name))}
            onDoubleClick={() => focus.isolate(item.name, names)}
            onFocus={() => focus.hover(item.name)}
            onMouseEnter={() => focus.hover(item.name)}
            onMouseLeave={() => focus.hover(undefined)}
            title={item.name}
            type="button"
          >
            <i className={`sw ${item.mark}`} style={{ color: item.color }} />
            <span className="ln">{item.name}</span>
          </button>
        ) : (
          <span className="li" data-lens-part={item.part} key={item.name} role="listitem" title={item.name}>
            <i className={`sw ${item.mark}`} style={{ color: item.color }} />
            <span className="ln">{item.name}</span>
          </span>
        ),
      )}
      {rest.length ? (
        <button aria-expanded={false} className="more" onClick={() => setExpanded(true)} title={rest.map((item) => item.name).join('\n')} type="button">
          +{rest.length}
        </button>
      ) : null}
      {expanded && fits < items.length ? (
        <button aria-expanded className="more" onClick={() => setExpanded(false)} type="button">
          Less
        </button>
      ) : null}
      {focus?.hidden.size ? (
        <button className="lg-all" onClick={focus.showAll} type="button">
          Show all
        </button>
      ) : null}
    </div>
  );
}
